// Nejnižší cena za 30 dnů (Omnibus §12a) + rozbalovací graf vývoje ceny
// na detailu produktu.
//
// Shoptet historii cen nikde nenese — data jdou z denních BQ snapshotů
// reportingu (od 2026-06-02) přes noční sync na s1 a file-backed endpoint
// `GET /v1/{tenant}/price-history?code=` (search/api/history.mjs). Položka:
//   { code, omnibus?, low30?, from, to, points: [["YYYY-MM-DD", cena], …] }
//   `points` = change-pointy PRODEJNÍ ceny (jen dny změny), `omnibus` =
//   minimum v okně 30 dnů PŘED začátkem aktuální akce, `low30` = rolling
//   minimum 30 dnů (fallback pro slevy, které BQ nevidí jako akci).
//
// Zásady (rozhodnutí Tomáše 2026-08-31, mock schválen; rozvržení od
// 2026-09-01 dle `design_handoff_nakupni_box/README.md` §6–7):
//   • blok stojí POD tlačítkem do košíku jako poslední oddíl nákupní karty
//     (souhrn | mini sparkline | přepínač) a kreslí se JEN při reálné slevě
//     NA STRÁNCE (`card.discounted` z enhanceCta — subscribe proto až PO něm)
//     a jen když API hodnotu má; znění podle druhu: „…za 30 dnů před slevou"
//     (omnibus) / „…za posledních 30 dnů" (low30), dovětek „· aktuálně
//     nejnižší cena" jen když aktuální cena na minimum sedí;
//   • graf = rozbalovací panel pod souhrnem: schodová čára prodejní ceny,
//     čárkovaná linka nejnižší ceny, růžové pásmo probíhající akce (od
//     posledního POKLESU ceny — jen při slevě), osa Y v kulatých úrovních
//     (`axisRange`); staví se až při prvním otevření na skutečnou šířku;
//   • tichá degradace: bez konfigurace vyhledávání (Ú0), API mimo, prázdná
//     odpověď → nic se nekreslí, nula chyb v konzoli;
//   • datumy se parsují lokálně regexem — `new Date('YYYY-MM-DD')` je UTC
//     a posouvá den (stejná past jako v saleEnd.js).
//
// Čisté funkce (parseDay, pickLow, normalizePoints, saleBandFrom, axisRange,
// chartModel, sparklineModel)
// jsou exportované pro devcheck část A (Node bez prohlížeče) — nesmí sahat
// na DOM.

import { icon } from './icon.js';
import { formatPrice } from './productPrice.js';
import { priceHistory as fetchHistory } from './searchApi.js';
import { shoptetData } from '../shoptet-hooks.js';
import { currency, withCurrency } from './money.js';
import SHOP from '../../config/shop.js';
import L from '../locale/cs/priceHistory.js';

export const LABELS = L.labels;

const NBSP = '\u00A0';
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/** `YYYY-MM-DD` → lokální půlnoc, jinak `null` (UTC parse by posunul den). */
export function parseDay(value) {
  const m = DATE_RE.exec(String(value || ''));
  if (!m) return null;
  const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  return isNaN(d.getTime()) ? null : d;
}

/** Zobrazená nejnižší cena: omnibus má přednost, low30 je fallback. */
export function pickLow(item) {
  if (!item) return null;
  if (isFinite(item.omnibus) && item.omnibus > 0) return { value: item.omnibus, kind: 'omnibus' };
  if (isFinite(item.low30) && item.low30 > 0) return { value: item.low30, kind: 'low30' };
  return null;
}

/** Validní body vzestupně: `[{t: Date, price}]`; smetí se tiše zahodí. */
export function normalizePoints(points) {
  const out = [];
  (Array.isArray(points) ? points : []).forEach(function (p) {
    if (!Array.isArray(p) || p.length < 2) return;
    const t = parseDay(p[0]);
    const price = Number(p[1]);
    if (!t || !isFinite(price) || price <= 0) return;
    out.push({ t: t, price: price });
  });
  out.sort(function (a, b) {
    return a.t - b.t;
  });
  return out;
}

/**
 * Začátek pásma probíhající akce = datum posledního POKLESU ceny. Jen odhad
 * pro vizuál (API začátek akce nenese): poslední change-point je u zlevněného
 * produktu typicky start akční ceny. Jediný bod nebo poslední změna nahoru
 * → žádné pásmo.
 */
export function saleBandFrom(points) {
  const pts = normalizePoints(points);
  if (pts.length < 2) return null;
  const last = pts[pts.length - 1];
  return last.price < pts[pts.length - 2].price ? last.t : null;
}

/**
 * Rozsah osy Y zaokrouhlený na „kulaté" úrovně (handover nákupního boxu §7):
 * krok podle rozpětí cen (100 / 500 / 1 000 … Kč), `min` dolů, `max` nahoru,
 * a to VŽDY o kus za krajní cenu, ať čára neleží na mřížce.
 */
export function axisRange(pMin, pMax) {
  const span = Math.max(pMax - pMin, Math.max(1, pMax * 0.02));
  // krok = nejbližší kulatá hodnota, aby na ose vyšly ~3–6 úrovní
  const raw = span / 3;
  const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))));
  let step = mag;
  [1, 2, 2.5, 5, 10].some(function (m) {
    step = mag * m;
    return step >= raw;
  });
  const min = Math.floor((pMin - step) / step) * step;
  const max = Math.ceil((pMax + step) / step) * step;
  const levels = [];
  for (let v = max; v >= min - step / 2 && levels.length < 12; v -= step) levels.push(Math.round(v * 100) / 100);
  return { min: min, max: max, step: step, levels: levels };
}

/** „1 899" — částka bez měny, NBSP oddělovač tisíců (osy grafu). */
function fmtNum(value) {
  const rounded = Math.round(value * 100) / 100;
  return new Intl.NumberFormat(SHOP.shop.language, {
    maximumFractionDigits: rounded % 1 === 0 ? 0 : 2,
    minimumFractionDigits: 0,
  })
    .format(rounded)
    .replace(/\s/g, NBSP);
}

/** „2. 6." */
function tickDate(d) {
  return d.getDate() + '.' + NBSP + (d.getMonth() + 1) + '.';
}

/** „2. 6. 2026" (hlavička panelu). */
export function fullDate(d) {
  return d ? d.getDate() + '.' + NBSP + (d.getMonth() + 1) + '.' + NBSP + d.getFullYear() : '';
}

/**
 * Geometrie grafu pro šířku `width` (DOM-free — render dělá buildChartSvg).
 * Vrací `null`, když není co kreslit (žádný validní bod).
 *
 * @param {{points: Array, to?: string, omnibus?: number, low30?: number}} item
 * @param {{width?: number, height?: number, discounted?: boolean}} [opts]
 */
export function chartModel(item, opts) {
  const o = opts || {};
  const width = Math.max(220, Math.round(o.width || 510));
  const height = Math.round(o.height || 150);
  const pts = normalizePoints(item && item.points);
  if (!pts.length) return null;

  const last = pts[pts.length - 1];
  const toDay = parseDay(item && item.to);
  const t0 = pts[0].t.getTime();
  const t1 = Math.max(toDay ? toDay.getTime() : t0, last.t.getTime(), t0 + DAY_MS);

  let pMin = Infinity;
  let pMax = -Infinity;
  pts.forEach(function (p) {
    if (p.price < pMin) pMin = p.price;
    if (p.price > pMax) pMax = p.price;
  });
  // osa v kulatých úrovních (handover §7) — mřížka i popisky po celém kroku
  const axis = axisRange(pMin, pMax);
  const dMin = axis.min;
  const dMax = axis.max;

  const labels = axis.levels.map(fmtNum);
  const left = Math.max(
    34,
    10 +
      Math.round(
        6.2 *
          labels.reduce(function (max, s) {
            return Math.max(max, s.length);
          }, 0),
      ),
  );
  const plot = { x0: left, x1: width - 14, y0: 12, y1: height - 24 };
  const x = function (t) {
    return plot.x0 + ((t - t0) / (t1 - t0)) * (plot.x1 - plot.x0);
  };
  const y = function (p) {
    return plot.y0 + ((dMax - p) / (dMax - dMin)) * (plot.y1 - plot.y0);
  };
  const r1 = function (v) {
    return Math.round(v * 10) / 10;
  };

  // schody: M start, pak H (čas) + V (nová cena), poslední úsek dotažený doprava
  let path = 'M' + r1(x(t0)) + ' ' + r1(y(pts[0].price));
  for (let i = 1; i < pts.length; i += 1) {
    path += ' H' + r1(x(pts[i].t.getTime())) + ' V' + r1(y(pts[i].price));
  }
  path += ' H' + r1(plot.x1);

  // pásmo probíhající akce — jen při slevě na stránce a smysluplné šířce
  let band = null;
  if (o.discounted) {
    const saleT = saleBandFrom(item.points);
    if (saleT) {
      const bx = x(saleT.getTime());
      if (plot.x1 - bx >= 3) band = { x: r1(bx), w: r1(plot.x1 - bx) };
    }
  }

  // čárkovaná linka zobrazené nejnižší ceny + popisek (pod linkou, když je u stropu)
  const low = pickLow(item);
  let ref = null;
  if (low) {
    const ry = y(low.value);
    const short = width < 430;
    const name = low.kind === 'omnibus' ? (short ? LABELS.refOmnibusShort : LABELS.refOmnibus) : short ? LABELS.refLow30Short : LABELS.refLow30;
    ref = {
      y: r1(ry),
      label: name + ' · ' + withCurrency(fmtNum(low.value)),
      labelY: r1(ry < plot.y0 + 20 ? ry + 13 : ry - 6),
    };
  }

  // ticky: začátek dat, začátky měsíců, „dnes" u pravé hrany
  const ticks = [{ x: r1(plot.x0), label: tickDate(pts[0].t), anchor: 'start' }];
  const cursor = new Date(pts[0].t.getFullYear(), pts[0].t.getMonth() + 1, 1);
  while (cursor.getTime() < t1) {
    const tx = x(cursor.getTime());
    if (tx > plot.x0 + 40 && tx < plot.x1 - 44) ticks.push({ x: r1(tx), label: tickDate(cursor), anchor: 'middle' });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  ticks.push({ x: r1(plot.x1), label: LABELS.today, anchor: 'end' });

  const endY = y(last.price);
  const end = {
    x: r1(plot.x1),
    y: r1(endY),
    label: withCurrency(fmtNum(last.price)),
    labelX: r1(plot.x1 - 6),
    labelY: r1(endY - 11 < plot.y0 + 8 ? endY + 18 : endY - 11),
  };

  return {
    w: width,
    h: height,
    plot: plot,
    path: path,
    band: band,
    // vodorovná mřížka: úroveň nejbližší aktuální ceně je zvýrazněná
    grid: axis.levels.map(function (level, i) {
      return {
        y: r1(y(level)),
        label: labels[i],
        strong: Math.abs(level - last.price) < axis.step / 2,
        axis: i === axis.levels.length - 1,
      };
    }),
    ref: ref,
    ticks: ticks,
    end: end,
    aria: L.aria(fullDate(pts[0].t), fmtNum(pMin), fmtNum(pMax), fmtNum(last.price), currency().symbol),
  };
}

// ---------------------------------------------------------------------------
// Data: per-page cache kódu (na kód nejvýš jeden GET za život stránky)
// ---------------------------------------------------------------------------

const PENDING = new Map();

/** Položka historie pro kód, nebo `null`. Nikdy nehází. */
export function historyFor(code) {
  if (!code) return Promise.resolve(null);
  if (!PENDING.has(code)) {
    PENDING.set(
      code,
      fetchHistory(code).then(function (data) {
        const items = data && Array.isArray(data.items) ? data.items : [];
        const hit = items.filter(function (it) {
          return it && it.code === code;
        })[0];
        return hit || null;
      }),
    );
  }
  return PENDING.get(code);
}

/** Jen pro testy: podstrčit známou položku bez sítě. */
export function primeHistory(code, item) {
  PENDING.set(code, Promise.resolve(item || null));
}

// ---------------------------------------------------------------------------
// Render (jen prohlížeč)
// ---------------------------------------------------------------------------

const SVG_NS = 'http://www.w3.org/2000/svg';

function svgEl(name, attrs, className) {
  const el = document.createElementNS(SVG_NS, name);
  Object.keys(attrs || {}).forEach(function (k) {
    el.setAttribute(k, attrs[k]);
  });
  if (className) el.setAttribute('class', className);
  return el;
}

/** SVG grafu z modelu (`chartModel`). Barvy a písma řeší CSS třídy bs-ph__*. */
export function buildChartSvg(model) {
  const svg = svgEl('svg', {
    viewBox: '0 0 ' + model.w + ' ' + model.h,
    width: model.w,
    height: model.h,
    role: 'img',
    'aria-label': model.aria,
  });
  if (model.band) {
    svg.appendChild(
      svgEl('rect', { x: model.band.x, y: model.plot.y0, width: model.band.w, height: model.plot.y1 - model.plot.y0 }, 'bs-ph__band'),
    );
  }
  model.grid.forEach(function (g) {
    svg.appendChild(svgEl('line', { x1: model.plot.x0, y1: g.y, x2: model.plot.x1, y2: g.y }, 'bs-ph__grid' + (g.axis ? ' bs-ph__grid--axis' : '')));
    const label = svgEl('text', { x: model.plot.x0 - 6, y: g.y + 4, 'text-anchor': 'end' }, 'bs-ph__ylabel' + (g.strong ? ' bs-ph__ylabel--strong' : ''));
    label.textContent = g.label;
    svg.appendChild(label);
  });
  if (model.ref) {
    svg.appendChild(svgEl('line', { x1: model.plot.x0, y1: model.ref.y, x2: model.plot.x1, y2: model.ref.y }, 'bs-ph__ref'));
    const label = svgEl('text', { x: model.plot.x0, y: model.ref.labelY }, 'bs-ph__reflabel');
    label.textContent = model.ref.label;
    svg.appendChild(label);
  }
  svg.appendChild(svgEl('path', { d: model.path, fill: 'none' }, 'bs-ph__line'));
  svg.appendChild(svgEl('circle', { cx: model.end.x, cy: model.end.y, r: 4.5 }, 'bs-ph__dot'));
  const endLabel = svgEl('text', { x: model.end.labelX, y: model.end.labelY, 'text-anchor': 'end' }, 'bs-ph__endlabel');
  endLabel.textContent = model.end.label;
  svg.appendChild(endLabel);
  model.ticks.forEach(function (t) {
    svg.appendChild(svgEl('line', { x1: t.x, y1: model.plot.y1, x2: t.x, y2: model.plot.y1 + 3 }, 'bs-ph__tickline'));
    const label = svgEl('text', { x: t.x, y: model.h - 9, 'text-anchor': t.anchor }, 'bs-ph__tick');
    label.textContent = t.label;
    svg.appendChild(label);
  });
  return svg;
}

const SPARK = { w: 120, h: 34, x0: 2, x1: 118, y0: 8, y1: 26 };

/**
 * Mini sparkline vedle souhrnného řádku (handover §6): schodová čára celého
 * období + tečka na aktuální ceně. `null`, když není co kreslit.
 */
export function sparklineModel(item) {
  const pts = normalizePoints(item && item.points);
  if (!pts.length) return null;
  const toDay = parseDay(item && item.to);
  const t0 = pts[0].t.getTime();
  const t1 = Math.max(toDay ? toDay.getTime() : t0, pts[pts.length - 1].t.getTime(), t0 + DAY_MS);
  let pMin = Infinity;
  let pMax = -Infinity;
  pts.forEach(function (p) {
    if (p.price < pMin) pMin = p.price;
    if (p.price > pMax) pMax = p.price;
  });
  const r1 = function (v) {
    return Math.round(v * 10) / 10;
  };
  const x = function (t) {
    return SPARK.x0 + ((t - t0) / (t1 - t0)) * (SPARK.x1 - SPARK.x0);
  };
  // plochá historie kreslí čáru uprostřed pásma, ne u horní hrany
  const y = function (p) {
    return pMax === pMin ? (SPARK.y0 + SPARK.y1) / 2 : SPARK.y0 + ((pMax - p) / (pMax - pMin)) * (SPARK.y1 - SPARK.y0);
  };
  let d = 'M' + r1(x(t0)) + ' ' + r1(y(pts[0].price));
  for (let i = 1; i < pts.length; i += 1) d += ' H' + r1(x(pts[i].t.getTime())) + ' V' + r1(y(pts[i].price));
  d += ' H' + SPARK.x1;
  return { w: SPARK.w, h: SPARK.h, path: d, end: { x: SPARK.x1, y: r1(y(pts[pts.length - 1].price)) } };
}

/** SVG mini sparkline z modelu (`sparklineModel`). Barvy řeší CSS bs-ph__*. */
export function buildSparkSvg(model) {
  const svg = svgEl('svg', {
    viewBox: '0 0 ' + model.w + ' ' + model.h,
    width: model.w,
    height: model.h,
    'aria-hidden': 'true',
    focusable: 'false',
  });
  svg.appendChild(svgEl('path', { d: model.path, fill: 'none' }, 'bs-ph__spark-line'));
  svg.appendChild(svgEl('circle', { cx: model.end.x, cy: model.end.y, r: 3.4 }, 'bs-ph__spark-dot'));
  return svg;
}

// ---------------------------------------------------------------------------
// Mount do nákupní karty detailu
// ---------------------------------------------------------------------------

function node(tag, className, text) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text) el.textContent = text;
  return el;
}

/**
 * Řádek + rozbalovací graf v nákupní kartě. Volat AŽ PO `enhanceCta` —
 * viditelnost visí na `card.discounted`, který plní jeho render, a subscribe
 * pořadí na watchi rozhoduje o čerstvosti.
 */
export function mountPriceHistory(watch, card) {
  if (!card || !card.priceCol) return;

  let ui = null;
  let item = null; // aktuální položka API (null = nic)
  let currentCode = '';
  let open = false;
  let builtKey = ''; // kód|šířka, pro který je graf postavený
  let resizeObserver = null;
  let lastState = watch.state;

  // Kód čteme živě — nativní span `tr.p-code td span` se v buildMeta PŘESOUVÁ
  // do `.bs-meta__code` (reference zůstává), u variant ho může přepsat default
  // JS. Fallback = kód z dataLayeru (chování jako saleEnd).
  function codeOf() {
    const el =
      document.querySelector('.bs-meta__code span:not(.bs-meta__label)') ||
      document.querySelector('#product-detail-form tr.p-code td span');
    const text = el && el.textContent ? el.textContent.trim() : '';
    if (text) return text;
    const product = shoptetData('product');
    return (product && product.code) || '';
  }

  function buildUi() {
    // Blok stojí POD tlačítkem do košíku (handover nákupního boxu §6) — dřív
    // visel jako řádek v cenovém sloupci, kde na sparkline ani graf nebylo místo.
    const box = node('div', 'bs-buy__ph');
    box.hidden = true;
    const row = node('div', 'bs-buy__ph-row');
    const note = node('div', 'bs-buy__omnibus-note');
    const label = node('span', 'bs-buy__omnibus-label');
    const val = node('span', 'bs-buy__omnibus-val');
    const value = node('b');
    const minNote = node('span', 'bs-buy__omnibus-min', LABELS.atMin);
    minNote.hidden = true;
    val.appendChild(value);
    val.appendChild(document.createTextNode(' '));
    val.appendChild(minNote);
    note.appendChild(label);
    note.appendChild(val);
    row.appendChild(note);

    const spark = node('span', 'bs-buy__ph-spark');
    row.appendChild(spark);

    const toggle = node('button', 'bs-buy__ph-toggle');
    toggle.type = 'button'; // uvnitř #product-detail-form — bez typu by odesílal košík
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', 'bs-ph-panel');
    toggle.appendChild(node('span', null, LABELS.toggle));
    toggle.appendChild(icon('chevron-down'));
    row.appendChild(toggle);
    box.appendChild(row);

    const panel = node('div', 'bs-ph');
    panel.id = 'bs-ph-panel';
    panel.hidden = true;
    const head = node('div', 'bs-ph__head');
    const title = node('span', 'bs-ph__title', LABELS.title);
    const period = node('span', 'bs-ph__period');
    head.appendChild(title);
    head.appendChild(period);
    const chart = node('div', 'bs-ph__chart');
    const caption = node('div', 'bs-ph__caption');
    panel.appendChild(head);
    panel.appendChild(chart);
    panel.appendChild(caption);
    box.appendChild(panel);

    // za pás CTA, ale před upsell (ten je poslední blok karty)
    const cta = card.querySelector('.bs-buy__cta');
    if (cta) cta.insertAdjacentElement('afterend', box);
    else card.appendChild(box);

    toggle.addEventListener('click', function () {
      setOpen(!open);
    });

    return {
      box: box,
      label: label,
      value: value,
      minNote: minNote,
      spark: spark,
      toggle: toggle,
      panel: panel,
      period: period,
      chart: chart,
      caption: caption,
    };
  }

  /** Mini sparkline se překresluje jen při změně kódu (šířka je fixní). */
  function renderSpark() {
    if (!ui || !item) return;
    if (ui.spark.dataset.code === currentCode) return;
    const model = sparklineModel(item);
    ui.spark.textContent = '';
    if (model) ui.spark.appendChild(buildSparkSvg(model));
    ui.spark.dataset.code = currentCode;
  }

  function renderChart() {
    if (!ui || !item) return;
    const width = ui.chart.clientWidth || 510;
    const sale = !!(card.discounted || card.priceAction);
    const key = currentCode + '|' + width + '|' + (sale ? 1 : 0);
    if (key === builtKey) return; // guard smyčky ResizeObserveru — kreslit jen při změně
    const model = chartModel(item, { width: width, discounted: sale });
    if (!model) return;
    ui.chart.textContent = '';
    ui.chart.appendChild(buildChartSvg(model));
    const from = parseDay(item.from) || normalizePoints(item.points)[0].t;
    ui.period.textContent = LABELS.from + ' ' + fullDate(from);
    ui.caption.textContent = LABELS.caption + (model.band ? ' · ' + LABELS.captionBand : '');
    builtKey = key;
  }

  function setOpen(next) {
    if (!ui || open === next) return;
    open = next;
    ui.panel.hidden = !open;
    ui.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    ui.toggle.classList.toggle('is-open', open);
    if (open) {
      renderChart();
      if (!resizeObserver && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(function () {
          if (open) renderChart();
        });
      }
      if (resizeObserver) resizeObserver.observe(ui.panel);
    } else if (resizeObserver) {
      resizeObserver.disconnect();
    }
  }

  function apply(state) {
    lastState = state;
    // Bez slevy na stránce se API neptáme vůbec (většina katalogu) — řádek by
    // se stejně nekreslil; sleva se objeví nejdřív s emitem watche (varianty).
    // Sleva = přeškrtnutá cena, nebo akční cena platformy bez ní (`priceAction`,
    // výprodej bez běžné ceny — plán kampaní 6. 10., mock M10).
    const discounted = !!(card.discounted || card.priceAction);
    if (!discounted) {
      if (ui) {
        ui.box.hidden = true;
        setOpen(false);
      }
      return;
    }
    const code = codeOf();
    if (code && code !== currentCode) {
      currentCode = code;
      item = null;
      historyFor(code).then(function (it) {
        if (code !== currentCode) return; // mezitím přepnutá varianta
        item = it;
        apply(lastState);
      });
    }
    const low = item ? pickLow(item) : null;
    const show = !!(discounted && low);
    if (show && !ui) ui = buildUi();
    if (!ui) return;
    ui.box.hidden = !show;
    if (!show) {
      setOpen(false);
      return;
    }
    ui.label.textContent = low.kind === 'omnibus' ? LABELS.omnibus : LABELS.low30;
    ui.value.textContent = formatPrice(low.value, state.currency);
    // „aktuálně nejnižší cena" jen když aktuální cena na minimum opravdu sedí
    ui.minNote.hidden = !(isFinite(state.unit) && Math.round(state.unit) <= Math.round(low.value));
    renderSpark();
    if (open) renderChart();
  }

  watch.subscribe(apply);
}
